import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { RATE_WIDGET_IDS, CATEGORY_ENDPOINT_MAP } from "../utils/widgetConstants";
import { resolveDatePreset } from "../utils/datePresetUtils";
import { resolveChartData } from "../utils/chartDataUtils";

const TASK_LIST_WIDGET_ID = "TASKS";

class WidgetDataStore {
  @observable widgetData = {};
  @observable loadingByCategory = {};
  @observable activeDateFilter = "7d";
  @observable activeMemberIds = [];

  @action
  setDateFilter = preset => {
    this.activeDateFilter = preset;
  };

  @action
  setMemberIds = ids => {
    this.activeMemberIds = ids || [];
  };

  @action
  setLoadingForCategory = (category, value) => {
    this.loadingByCategory = { ...this.loadingByCategory, [category]: value };
  };

  @action
  setWidgetData = (widgetId, data) => {
    this.widgetData = { ...this.widgetData, [widgetId]: data };
  };

  getWidgetData = widgetId => {
    return this.widgetData[widgetId] || null;
  };

  isCategoryLoading = category => {
    return !!this.loadingByCategory[category];
  };

  @action
  clearWidgetData = widgetId => {
    const updated = { ...this.widgetData };
    delete updated[widgetId];
    this.widgetData = updated;
  };

  fetchSingleWidget = async widget => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = resolveDatePreset(this.activeDateFilter);
    const memberIds = this.activeMemberIds;
    if (widget.widgetId === TASK_LIST_WIDGET_ID) {
      this.setLoadingForCategory(TASK_LIST_WIDGET_ID, true);
      await this.fetchTaskList(toDate, memberIds);
    } else if (RATE_WIDGET_IDS.has(widget.widgetId)) {
      this.setLoadingForCategory(widget.widgetId, true);
      await this.fetchRateSeriesData(widget.widgetId, fromDate, toDate, memberIds);
    } else {
      this.setLoadingForCategory(widget.category, true);
      await this.fetchCategoryData(widget.category, [widget.widgetId], fromDate, toDate, memberIds);
    }
  };

  @action
  fetchAllWidgetData = async widgets => {
    if (!widgets?.length) return;

    const { fromDate, toDate } = resolveDatePreset(this.activeDateFilter);
    const memberIds = this.activeMemberIds;

    // Group non-rate widgets by category
    const categoryMap = {};
    const rateWidgets = [];
    let hasTaskListWidget = false;

    widgets.forEach(w => {
      if (w.widgetId === TASK_LIST_WIDGET_ID) {
        hasTaskListWidget = true;
        this.setLoadingForCategory(TASK_LIST_WIDGET_ID, true);
      } else if (RATE_WIDGET_IDS.has(w.widgetId)) {
        rateWidgets.push(w);
        this.setLoadingForCategory(w.widgetId, true);
      } else {
        if (!categoryMap[w.category]) categoryMap[w.category] = [];
        categoryMap[w.category].push(w.widgetId);
      }
    });

    Object.keys(categoryMap).forEach(cat => this.setLoadingForCategory(cat, true));

    const categoryFetches = Object.entries(categoryMap).map(
      ([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds)
    );

    const rateFetches = rateWidgets.map(w =>
      this.fetchRateSeriesData(w.widgetId, fromDate, toDate, memberIds)
    );

    const taskListFetch = hasTaskListWidget ? [this.fetchTaskList(toDate, memberIds)] : [];

    await Promise.all([...categoryFetches, ...rateFetches, ...taskListFetch]);
  };

  fetchCategoryData = async (category, widgetIds, fromDate, toDate, memberIds) => {
    const urlKey = CATEGORY_ENDPOINT_MAP[category];
    if (!urlKey || !URLS[urlKey]) return;

    try {
      const res = await makeApi({
        url: URLS[urlKey],
        method: "POST",
        data: { widgetIds, fromDate, toDate, memberIds }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchCategoryData(${category}) failed`, data?.error);
        toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
        return;
      }
      if (data) {
        widgetIds.forEach(widgetId => {
          this.setWidgetData(widgetId, { ...data, _category: category });
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchCategoryData(${category}) error`, err);
      toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
    } finally {
      this.setLoadingForCategory(category, false);
    }
  };

  fetchTaskList = async (toDate, memberIds) => {
    try {
      const res = await makeApi({
        url: URLS.getTaskFilter,
        method: "POST",
        data: {
          pageNo: 1,
          limit: 5,
          taskType: null,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          status: "ACTIVE",
          dateFilter: { toDate },
          memberIds: memberIds || [],
          sequenceIds: [],
          tagIds: [],
          contactStage: [],
          contactFields: []
        }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error("DashboardV2: fetchTaskList failed", data?.error);
        return;
      }
      this.setWidgetData(TASK_LIST_WIDGET_ID, { tasks: data?.tasks || [], totalPages: data?.totalPages || 0 });
    } catch (err) {
      console.error("DashboardV2: fetchTaskList error", err);
    } finally {
      this.setLoadingForCategory(TASK_LIST_WIDGET_ID, false);
    }
  };

  fetchRateSeriesData = async (widgetId, fromDate, toDate, memberIds) => {
    try {
      const res = await makeApi({
        url: URLS.getDashboardWidgetsRateSeries,
        method: "POST",
        data: { widgetId, fromDate, toDate, memberIds }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) failed`, data?.error);
        toasterState.setToastMsg(data?.error || "Failed to load rate widget data", "fail");
        return;
      }
      if (data) {
        const chartData = resolveChartData(data, this.activeDateFilter);
        this.setWidgetData(widgetId, {
          currentRate: data.currentRate,
          previousRate: data.previousRate,
          trend: data.trend,
          chartData,
          series: data.series || [],
          _isRate: true
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) error`, err);
      toasterState.setToastMsg("Failed to load rate widget data", "fail");
    } finally {
      this.setLoadingForCategory(widgetId, false);
    }
  };
}

const widgetDataStore = new WidgetDataStore();
export { widgetDataStore };
export default widgetDataStore;
